import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useI18n } from "../../lib/i18n.jsx";
import { trackSubscribe } from "../../lib/analytics.js";
import { Mail } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Newsletter() {
  const { t, lang } = useI18n();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const trimmedName = name.trim();
    if (!trimmedName) return;
    if (!email || !/^\S+@\S+\.\S+$/.test(email)) return;
    setLoading(true);
    try {
      await axios.post(`${API}/newsletter`, { email, name: trimmedName, lang });
      trackSubscribe();
      toast.success(t.newsletter.success);
      setName("");
      setEmail("");
    } catch (err) {
      toast.error(t.newsletter.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="newsletter"
      data-testid="newsletter-section"
      className="bg-cream py-20 md:py-28"
    >
      <div className="max-w-[1100px] mx-auto px-5 md:px-8">
        <div className="relative rounded-[2rem] overflow-hidden bg-winter text-cream p-8 md:p-14">
          {/* Decorative circle */}
          <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-summer/20 blur-3xl pointer-events-none" />
          <div className="absolute -bottom-20 -left-20 w-64 h-64 rounded-full bg-sun/10 blur-3xl pointer-events-none" />

          <div className="relative grid lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 bg-cream/10 border border-cream/20 rounded-full px-3 py-1 mb-4">
                <Mail className="w-3.5 h-3.5 text-summer" />
                <span className="text-xs font-semibold uppercase tracking-wider">
                  {t.newsletter.kicker}
                </span>
              </div>
              <h2
                className="font-display font-bold leading-tight mb-3"
                style={{ fontSize: "clamp(1.75rem, 3.5vw, 2.75rem)" }}
              >
                {t.newsletter.title}
              </h2>
              <p className="text-cream/80 text-base md:text-lg max-w-md">
                {t.newsletter.subtitle}
              </p>
            </div>

            <div className="lg:col-span-5">
              <form onSubmit={submit} className="flex flex-col gap-3" data-testid="newsletter-form">
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t.newsletter.placeholder_name}
                  required
                  minLength={1}
                  maxLength={80}
                  autoComplete="given-name"
                  data-testid="newsletter-name"
                  className="w-full px-5 py-4 bg-cream text-winter placeholder:text-winter/45 rounded-full outline-none border-2 border-transparent focus:border-summer font-medium"
                />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t.newsletter.placeholder}
                  required
                  autoComplete="email"
                  data-testid="newsletter-input"
                  className="w-full px-5 py-4 bg-cream text-winter placeholder:text-winter/45 rounded-full outline-none border-2 border-transparent focus:border-summer font-medium"
                />
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="newsletter-submit"
                  className="btn-pill btn-pill-primary w-full justify-center py-4 text-base disabled:opacity-60"
                >
                  {loading ? "..." : t.newsletter.cta}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
